import React, { useState } from 'react';

import { FiltrosDoLogGeral, OpcoesDoLogGeral, ROTULO_DO_TIPO_DE_ATOR, TipoDeAtor } from '../../../../api/auditLogsApi';
import ModalSync from '../../../../components/sync-painel/ModalSync';

interface Props {
  /** O que está aplicado na tela: o modal REABRE com isso (BUG-V43). */
  filtrosAplicados: FiltrosDoLogGeral;
  /** `null` quando a carga das opções falhou: os campos de módulo e ação viram texto livre. */
  opcoes: OpcoesDoLogGeral | null;
  onAplicar: (filtros: FiltrosDoLogGeral) => void;
  onClose: () => void;
}

/** `2026-10-24T12:00:00.000Z` → `2026-10-24` para o input de data; e o caminho de volta. */
const paraInput = (iso?: string) => (iso ? iso.slice(0, 10) : '');
const inicioDoDia = (d: string) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const fimDoDia = (d: string) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);

/**
 * Filtros do log geral (transversal/at-91). Padrão do sistema: estado LOCAL, só sai no "Aplicar",
 * montagem condicional pelo chamador — o `useState` inicial é a única fonte, e o "Cancelar" desfaz
 * de verdade.
 */
const FiltroLogGeralModal: React.FC<Props> = ({ filtrosAplicados, opcoes, onAplicar, onClose }) => {
  const [de, setDe] = useState(paraInput(filtrosAplicados.de));
  const [ate, setAte] = useState(paraInput(filtrosAplicados.ate));
  const [pessoa, setPessoa] = useState(filtrosAplicados.pessoa ?? '');
  const [atorTipo, setAtorTipo] = useState<TipoDeAtor | ''>(filtrosAplicados.atorTipo ?? '');
  const [modulo, setModulo] = useState(filtrosAplicados.modulo ?? '');
  const [acao, setAcao] = useState(filtrosAplicados.acao ?? '');
  const [q, setQ] = useState(filtrosAplicados.q ?? '');
  const [entidade, setEntidade] = useState(filtrosAplicados.entidade ?? '');
  const [entidadeId, setEntidadeId] = useState(filtrosAplicados.entidadeId ?? '');
  const [soFalhas, setSoFalhas] = useState(!!filtrosAplicados.soFalhas);
  const [incluirNegocio, setIncluirNegocio] = useState(!!filtrosAplicados.incluirNegocio);

  const limpar = () => {
    setDe('');
    setAte('');
    setPessoa('');
    setAtorTipo('');
    setModulo('');
    setAcao('');
    setQ('');
    setEntidade('');
    setEntidadeId('');
    setSoFalhas(false);
    setIncluirNegocio(false);
  };

  const aplicar = () => {
    onAplicar({
      de: inicioDoDia(de),
      ate: fimDoDia(ate),
      pessoa: pessoa.trim() || undefined,
      atorTipo: atorTipo || undefined,
      modulo: modulo.trim() || undefined,
      acao: acao.trim() || undefined,
      q: q.trim() || undefined,
      entidade: entidade.trim() || undefined,
      entidadeId: entidadeId.trim() || undefined,
      soFalhas: soFalhas || undefined,
      incluirNegocio: incluirNegocio || undefined,
    });
  };

  const tipos = opcoes?.tiposDeAtor ?? (Object.keys(ROTULO_DO_TIPO_DE_ATOR) as TipoDeAtor[]).map((valor) => ({ valor, rotulo: ROTULO_DO_TIPO_DE_ATOR[valor] }));

  return (
    <ModalSync
      titulo="Filtros do log"
      subtitulo="Os filtros se combinam entre si"
      onClose={onClose}
      largura="min(640px, 94vw)"
      footer={
        <>
          <button type="button" className="filtro-btn-secondary" onClick={limpar}>
            Limpar filtros
          </button>
          <button type="button" className="filtro-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="filtro-btn-primary" onClick={aplicar}>
            Aplicar Filtros
          </button>
        </>
      }
    >
      <div className="filtro-section">
        <h3 className="filtro-section-title">Período</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label htmlFor="log-de">De</label>
            <input id="log-de" type="date" className="premium-text-input" value={de} onChange={(e) => setDe(e.target.value)} />
          </div>
          <div className="filtro-field">
            <label htmlFor="log-ate">Até</label>
            <input id="log-ate" type="date" className="premium-text-input" value={ate} onChange={(e) => setAte(e.target.value)} />
          </div>
        </div>
        <p className="filtro-field-hint">Sem período, a lista mostra os últimos 7 dias.</p>
      </div>

      <div className="filtro-section">
        <h3 className="filtro-section-title">Quem</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label htmlFor="log-pessoa">Pessoa</label>
            <input id="log-pessoa" className="premium-text-input" placeholder="Parte do nome" value={pessoa} onChange={(e) => setPessoa(e.target.value)} />
          </div>
          <div className="filtro-field">
            <label htmlFor="log-ator">Tipo</label>
            <div className="select-wrapper">
              <select id="log-ator" value={atorTipo} onChange={(e) => setAtorTipo(e.target.value as TipoDeAtor | '')}>
                <option value="">Todos</option>
                {tipos.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="filtro-section">
        <h3 className="filtro-section-title">O quê</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label htmlFor="log-modulo">Módulo</label>
            {opcoes ? (
              <div className="select-wrapper">
                <select id="log-modulo" value={modulo} onChange={(e) => setModulo(e.target.value)}>
                  <option value="">Todos</option>
                  {opcoes.modulos.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <input id="log-modulo" className="premium-text-input" value={modulo} onChange={(e) => setModulo(e.target.value)} />
            )}
          </div>
          <div className="filtro-field">
            <label htmlFor="log-acao">Ação</label>
            <input
              id="log-acao"
              className="premium-text-input"
              list={opcoes ? 'log-acoes' : undefined}
              placeholder="Ex.: cars.liberar, ou cars. para todas"
              value={acao}
              onChange={(e) => setAcao(e.target.value)}
            />
            {opcoes && (
              <datalist id="log-acoes">
                {opcoes.acoes.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            )}
          </div>
        </div>
        <div className="filtro-field">
          <label htmlFor="log-q">Texto no resumo</label>
          <input id="log-q" className="premium-text-input" placeholder="Ex.: liberação" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="filtro-section">
        <h3 className="filtro-section-title">Registro</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label htmlFor="log-entidade">Tipo de registro</label>
            <input id="log-entidade" className="premium-text-input" placeholder="Ex.: car, user" value={entidade} onChange={(e) => setEntidade(e.target.value)} />
          </div>
          <div className="filtro-field">
            <label htmlFor="log-entidade-id">Identificador</label>
            <input id="log-entidade-id" className="premium-text-input" value={entidadeId} onChange={(e) => setEntidadeId(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="filtro-section">
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={soFalhas} onChange={(e) => setSoFalhas(e.target.checked)} />
          Só o que falhou ou foi recusado
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input type="checkbox" checked={incluirNegocio} onChange={(e) => setIncluirNegocio(e.target.checked)} />
          Mostrar também o que cada ação gravou
        </label>
        <p className="filtro-field-hint">
          Sem esta opção, cada ação aparece numa linha só, e o que ela gravou fica no detalhe.
        </p>
      </div>
    </ModalSync>
  );
};

export default FiltroLogGeralModal;
